import type { ComparisonData } from "../types";

export const midjourneyComparison: ComparisonData = {
  slug: "midjourney-vs-todo-ai",
  competitorName: "Midjourney",
  competitorCategory: "AI Image Generator",
  todoAiFlowPath: "/generate-image",
  title: "Midjourney vs TodoAI — Which AI Image Generator Is Right for You?",
  metaDescription:
    "Side-by-side comparison of Midjourney and TodoAI in 2026: pricing, Hebrew support, ease of use, included flows, and where each tool wins. Updated May 2026.",

  introTodoAi:
    "TodoAI is a Hebrew-first AI studio that wraps best-in-class image and video models behind opinionated, single-purpose flows. No prompt engineering — pick a flow (Image Generator, Product Studio, Profile Photo, Talking Avatar), drop in your input, and get a polished result. The interface is fully bilingual (Hebrew with RTL + English).",

  introCompetitor:
    "Midjourney is a US-based AI image generator known for its distinctive artistic style and high-quality outputs. It runs primarily through a Discord interface and a newer web app, requires prompt-engineering skill to get specific results, and is built around English-language prompts. There is no native Hebrew interface or RTL support.",

  rows: [
    {
      label: "Starting price",
      todoAi: "₪0 / $0 — free with 100 credits/month, no credit card",
      competitor: "$10/month (Basic) — no free tier",
      highlight: "todoAi",
    },
    {
      label: "Hebrew interface",
      todoAi: "Yes — full Hebrew UI + RTL layout",
      competitor: "No — English only",
      highlight: "todoAi",
    },
    {
      label: "Hebrew prompts",
      todoAi: "Yes — type in Hebrew, get Hebrew-aware results",
      competitor: "Partial — accepts Hebrew but optimized for English",
      highlight: "todoAi",
    },
    {
      label: "Prompt engineering required",
      todoAi: "No — opinionated flows handle prompt construction",
      competitor: "Yes — quality depends heavily on user's prompt skill",
      highlight: "todoAi",
    },
    {
      label: "Interface",
      todoAi: "Web app — form-style flows",
      competitor: "Discord bot + web app",
      highlight: "tie",
    },
    {
      label: "Included flows beyond raw image gen",
      todoAi: "9 flows: image, product shots, profile photo, talking avatar, text-to-video, etc.",
      competitor: "Image generation only",
      highlight: "todoAi",
    },
    {
      label: "Maximum resolution",
      todoAi: "Up to 4K",
      competitor: "Up to 2048×2048 (Standard), higher with upscale",
      highlight: "tie",
    },
    {
      label: "Talking-avatar / video",
      todoAi: "Yes — built in",
      competitor: "No",
      highlight: "todoAi",
    },
    {
      label: "Embeddable AI bot for your website",
      todoAi: "Yes — Bot Builder included on Pro/Creator plans",
      competitor: "No",
      highlight: "todoAi",
    },
    {
      label: "Best for",
      todoAi: "Hebrew-speaking creators and small businesses who want fast, branded outputs without learning prompt syntax",
      competitor: "English-speaking artists and designers who want maximum creative control via detailed prompts",
      highlight: "tie",
    },
  ],

  stats: [
    {
      value: "100 credits",
      description: "Free monthly allotment on TodoAI — enough to try every flow",
    },
    {
      value: "9 flows",
      description: "Single-purpose tools on TodoAI: image, product, profile, video, avatar, and more",
    },
    {
      value: "₪35 → ₪190",
      description: "TodoAI paid-plan range (Starter → Creator), all with full editor + video + 4K",
    },
    {
      value: "0",
      description: "Free-tier credits offered by Midjourney — a paid subscription is required to generate anything",
      source: "https://www.midjourney.com/pricing",
      sourceLabel: "Midjourney pricing page",
    },
  ],

  faqs: [
    {
      question: "Is TodoAI cheaper than Midjourney?",
      answer:
        "TodoAI offers a free tier (100 credits/month, no credit card), while Midjourney requires a paid plan starting at $10/month. For light or experimental use, TodoAI is functionally free; for heavy daily use, the paid tiers are roughly comparable.",
    },
    {
      question: "Does TodoAI support Hebrew prompts?",
      answer:
        "Yes — TodoAI is built Hebrew-first. The interface, prompts, generated text overlays, and all UI flow naturally in Hebrew with full RTL (right-to-left) layout. Midjourney only supports English prompts effectively.",
    },
    {
      question: "Can I generate videos on either platform?",
      answer:
        "Only TodoAI generates videos natively (text-to-video, animate-a-photo, baseball-trend, talking-avatar). Midjourney is image-only.",
    },
    {
      question: "Do I need to learn prompt engineering on TodoAI?",
      answer:
        "No. TodoAI's flows handle prompt construction internally — you fill in form-style inputs (subject, style, background) and the system builds the optimal prompt for the underlying model. Midjourney requires the user to compose detailed prompts and learn its parameter syntax.",
    },
    {
      question: "Which one is better for my e-commerce product photos?",
      answer:
        "TodoAI's Product Studio and Product Creative flows are purpose-built for e-commerce — upload one product photo and get 4 studio-quality variants in seconds. Midjourney can produce product visuals but requires a multi-step prompt-engineering process to match results.",
    },
  ],

  lastUpdated: "2026-05-26",
};
